import { LoadingOutlined } from "@ant-design/icons";
import { notification, Pagination, Select } from "antd";
import AccessControl from "components/common/AccessControl/accessControl";
import { clientRoles, ENTITY_TYPE, ROLE, roleLabels, userRoleType } from "constants/phase";
import { isUndefined } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useLocation, useOutletContext, useParams } from "react-router-dom";
import { getOrganizationUsers } from "store/organization/duck";
import { updateOrganizationMember } from "store/organizationMember/duck";
import { getWorkspaceDetails, getWorkspaceUsers } from "store/workspace/duck";
import { updateWorkspaceMember } from "store/workspaceMember/duck";

import CustomModal from "../CustomModal";
import ActionsContainer from "./ActionsContainer";
import styles from "./style.module.scss";

const listColumns = [
  {
    title: "NAME",
    dataIndex: "name",
    key: "name",
  },
  {
    title: "EMAIL ID",
    key: "email",
    dataIndex: "email",
  },
  {
    title: "ROLE",
    key: "role",
    dataIndex: "role",
  },
  {
    title: "ACTIONS",
    key: "actions",
    dataIndex: "actions",
  },
];

const roleSelectOptions = (role) => {
  if (Object.values(clientRoles).includes(role)) {
    return Object.values(clientRoles).map((role) => ({ label: roleLabels[role], value: role }));
  } else if (role === userRoleType.generalOps || role === userRoleType.privilegeOps) {
    return [{ value: role, label: roleLabels[role] }];
  }

  return [];
};

const mapDispatchToProps = {
  getOrganizationUsers,
  updateOrganizationMember,
  updateWorkspaceMember,
  getWorkspaceUsers,
  getWorkspaceDetails,
};

const pageSize = 3;

const UserManagementModal = ({
  setShowModal,
  entity,
  entityType,
  role,
  setShowAddUserModal,
  getOrganizationUsers,
  setShowRemoveUserModal,
  updateOrganizationMember,
  closeAndFetchModal,
  updateWorkspaceMember,
  getWorkspaceUsers,
  getWorkspaceDetails,
  maxNumberOfUsers,
  totalUserCount,
}) => {
  const [loading, setLoading] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const { currentRole } = useOutletContext();
  const { id } = useParams();
  const { pathname } = useLocation();

  const updateUserRole = (userId, role) => {
    setUsers((prev) =>
      prev.map((user) => {
        if (user.userId === userId) {
          return { ...user, role };
        }

        return user;
      }),
    );
  };

  const updateUserHandler = useCallback(
    async (user) => {
      try {
        if (entityType === ENTITY_TYPE.ORGANIZATION) {
          const payload = {
            organizationMemberId: user?.organizationMemberId,
            role: user?.role,
          };

          const response = await updateOrganizationMember(payload);

          if (response?.value?.status) {
            notification.success({ message: response?.value?.message });
          }
        } else {
          const payload = {
            workspaceMemberId: user?.workspaceMemberId,
            role: user?.role,
          };

          const response = await updateWorkspaceMember(payload);

          if (response?.value?.status) {
            notification.success({ message: response?.value?.message });
          }
        }

        closeAndFetchModal();
      } catch (error) {
        console.log(error);
        if (error.message) {
          notification.error({ message: error.message });
        }
      }
    },
    [closeAndFetchModal, entityType, updateOrganizationMember, updateWorkspaceMember],
  );

  const userRows = useMemo(() => {
    return users.map((user) => {
      return {
        key: user.userId,
        name: user?.name,
        email: user?.email,
        role: Object.values(clientRoles).includes(user?.role) ? (
          <Select
            disabled={currentRole === ROLE.USER || currentRole === ROLE.REVIEWER || currentRole === ROLE.ADMIN}
            showSearch={false}
            allowClear={false}
            optionFilterProp="children"
            className="custom-ant-select-v2"
            value={user?.role}
            onChange={(role) => updateUserRole(user.userId, role)}
            options={roleSelectOptions(user?.role)}
          />
        ) : (
          roleLabels[user?.role]
        ),
        actions: (
          <AccessControl code="actions_workspace_modal" page="organization-profile">
            <ActionsContainer
              updateUserRole={() => updateUserHandler(user)}
              setShowRemoveUserModal={() => {
                setShowModal(null);
                setShowRemoveUserModal(user);
              }}
            />
          </AccessControl>
        ),
      };
    });
  }, [setShowModal, setShowRemoveUserModal, updateUserHandler, users]);

  const fetchEntityUsers = useCallback(
    async (activePage) => {
      try {
        setLoading(true);
        let tempUsers;
        if (entityType === ENTITY_TYPE.ORGANIZATION) {
          const response = await getOrganizationUsers({
            organizationId: entity?.organizationId,
            role,
            pageSize,
            page: activePage,
            search: "",
          });

          if (response?.value?.status) {
            setUsers(response?.value?.users);
            setTotal(response?.value?.total);
          }
        } else {
          const response = await getWorkspaceUsers({
            workspaceId: entity?.workspaceId,
            role,
            pageSize,
            page: activePage,
            search: "",
          });

          if (response?.value?.status) {
            tempUsers = response?.value?.users?.filter((user) => user.role === role);
            setUsers(tempUsers);
            setTotal(response?.value?.total);
          }
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    },
    [entityType, getOrganizationUsers, entity, role, getWorkspaceUsers],
  );

  useEffect(() => {
    fetchEntityUsers(activePage);
  }, [activePage, fetchEntityUsers]);
  return (
    <>
      <CustomModal
        showModal={!!entity}
        setShowModal={setShowModal}
        modalClassName={styles.modal}
        contentLabel={`Manage ${role}`}
        modalBodyClassName={styles.modalBody}
        showFooter={false}
      >
        <div className={styles.formContainer}>
          <div className={styles.ctaContainer}>
            <div className={styles.organizationProfile}>
              <div className={styles.organizationLabel}>
                {!isUndefined(id) || pathname === "/workspace-management" ? "Workspace" : "Organization"}
              </div>
              <div className={styles.organizationInfoContainer}>
                <div className={styles.logo}>
                  <img className={styles.logoPreview} src={entity?.logo} alt="profile-preview" />
                </div>
                <div className={styles.name}>{entity?.displayName}</div>
              </div>
            </div>
            <AccessControl code="add_user_workspace" page="organization-profile">
              <button
                className={styles.addWorkspaceCta}
                onClick={() =>
                  maxNumberOfUsers > totalUserCount
                    ? (setShowAddUserModal(entity), setShowModal(null))
                    : notification.warning({
                        message: `You can add only ${maxNumberOfUsers} Users.`,
                      })
                }
              >
                Add new user
              </button>
            </AccessControl>
          </div>

          <div className={styles.container}>
            {loading ? (
              <LoadingOutlined style={{ fontSize: 30, align: "center", display: "inline-flex" }} spin />
            ) : (
              <div className={styles.tableContainer}>
                <div className={styles.table}>
                  <div className={styles.header}>
                    {listColumns.map((column, index) => (
                      <div key={index} className={styles.columnHeader}>
                        {column.title}
                      </div>
                    ))}
                  </div>
                  <div className={styles.rowContainer}>
                    {userRows.map((row, rowIndex) => (
                      <div key={rowIndex} className={styles.row}>
                        {listColumns.map((column, colIndex) => (
                          <div key={colIndex} className={styles.cell}>
                            {row[column.dataIndex]}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <Pagination
            showSizeChanger={false}
            className={styles.pagination}
            size="small"
            current={activePage}
            pageSize={pageSize}
            onChange={(activePage) => setActivePage(activePage)}
            total={total}
          />
        </div>
      </CustomModal>
    </>
  );
};

export default connect((/*state*/) => ({}), mapDispatchToProps)(UserManagementModal);
